import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ActiveSession } from '@/hooks/useDashboardData';
import { format, subMonths, startOfMonth, eachMonthOfInterval, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface DecisionTrendsChartProps {
  sessions: ActiveSession[];
}

export const DecisionTrendsChart = ({ sessions }: DecisionTrendsChartProps) => {
  const chartData = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = subMonths(now, 5);
    const months = eachMonthOfInterval({ start: sixMonthsAgo, end: now });

    return months.map((month) => {
      const monthStart = startOfMonth(month);
      const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);

      const decisionsInMonth = sessions.filter((session) => {
        try {
          const sessionDate = parseISO(session.created_at);
          return sessionDate >= monthStart && sessionDate <= monthEnd;
        } catch {
          return false;
        }
      });

      return {
        month: format(month, 'MMM', { locale: de }),
        entscheidungen: decisionsInMonth.length,
      };
    });
  }, [sessions]);

  if (sessions.length === 0) {
    return (
      <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
        Noch keine Daten verfügbar
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis 
          dataKey="month" 
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <YAxis 
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <Tooltip 
          contentStyle={{
            backgroundColor: 'hsl(var(--popover))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            color: 'hsl(var(--popover-foreground))',
          }}
        />
        <Line 
          type="monotone" 
          dataKey="entscheidungen" 
          stroke="hsl(var(--primary))" 
          strokeWidth={2}
          dot={{ fill: 'hsl(var(--primary))' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
